import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { resolveBlogImageSrc } from "../../../../utils/blogImages";
import { Check, Images, Keyboard, MousePointerClick, Search, Sparkles, Upload, X } from "lucide-react";

function basenameFromPath(imagePath) {
    const p = String(imagePath || "").trim();
    if (!p) return "";
    return p.split("/").filter(Boolean).pop() || p;
}

export default function CoverImageGalleryModal({
    open,
    imagePaths,
    selectedPath,
    loading,
    error,
    uploadError,
    uploadLabel,
    uploadDisabled,
    onClose,
    onPick,
    onRetry,
    onCoverFileSelect,
}) {
    const [query, setQuery] = useState("");
    const [hoverPath, setHoverPath] = useState(null);

    useEffect(() => {
        if (!open) {
            setQuery("");
            setHoverPath(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const id = window.setTimeout(() => {
            document.getElementById("abf-cover-search")?.focus();
        }, 80);
        return () => window.clearTimeout(id);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const filteredPaths = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return imagePaths;
        return imagePaths.filter((pathValue) => basenameFromPath(pathValue).toLowerCase().includes(q));
    }, [imagePaths, query]);

    const previewPath = useMemo(() => {
        if (!filteredPaths.length) return null;
        if (hoverPath && filteredPaths.includes(hoverPath)) return hoverPath;
        if (selectedPath && filteredPaths.includes(selectedPath)) return selectedPath;
        return filteredPaths[0];
    }, [filteredPaths, hoverPath, selectedPath]);

    const previewSrc = previewPath ? resolveBlogImageSrc(previewPath) : null;
    const previewName = previewPath ? basenameFromPath(previewPath) : "";

    const handleFileInput = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (file) onCoverFileSelect(file);
    };

    if (!open) return null;

    const totalImages = imagePaths.length;
    const shownCount = filteredPaths.length;
    const isFiltering = query.trim().length > 0;
    const previewMatchesSelection = Boolean(previewPath && selectedPath === previewPath);
    const previewIsForeignHover = Boolean(
        previewPath && hoverPath && hoverPath === previewPath && hoverPath !== selectedPath
    );

    return createPortal(
        <div className="abf-cover-modal" role="presentation">
            <button
                type="button"
                className="abf-cover-modal__backdrop"
                aria-label="Close gallery"
                onClick={onClose}
            />
            <div
                className="abf-cover-modal__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="abf-cover-modal-title"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="abf-cover-modal__header-glow" aria-hidden />
                <header className="abf-cover-modal__header">
                    <div className="abf-cover-modal__header-main">
                        <div className="abf-cover-modal__brand" aria-hidden>
                            <span className="abf-cover-modal__brand-icon">
                                <Images size={22} strokeWidth={2} />
                            </span>
                        </div>
                        <div className="abf-cover-modal__header-copy">
                            <div className="abf-cover-modal__title-row">
                                <h2 id="abf-cover-modal-title" className="abf-cover-modal__title">
                                    Cover gallery
                                </h2>
                                <span className="abf-cover-modal__title-sparkle">
                                    <Sparkles size={16} strokeWidth={2} aria-hidden />
                                </span>
                                <span className="abf-cover-modal__count">
                                    {isFiltering
                                        ? `${shownCount} / ${totalImages}`
                                        : `${totalImages} ${totalImages === 1 ? "asset" : "assets"}`}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button type="button" className="abf-cover-modal__close" onClick={onClose} aria-label="Close">
                        <X size={20} strokeWidth={2} aria-hidden />
                    </button>
                </header>

                <div className="abf-cover-modal__toolbar">
                    <div className="abf-cover-modal__toolbar-row">
                        <div className="abf-cover-modal__search-wrap">
                            <Search className="abf-cover-modal__search-icon" size={18} strokeWidth={2} aria-hidden />
                            <input
                                id="abf-cover-search"
                                type="search"
                                className="abf-cover-modal__search-input"
                                placeholder="Filter by filename…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                autoComplete="off"
                                aria-label="Filter images by filename"
                            />
                            {query ? (
                                <button
                                    type="button"
                                    className="abf-cover-modal__search-clear"
                                    onClick={() => setQuery("")}
                                    aria-label="Clear search"
                                >
                                    ×
                                </button>
                            ) : null}
                        </div>
                        <label className="abf-cover-modal__upload-btn">
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
                                onChange={handleFileInput}
                                disabled={uploadDisabled}
                            />
                            <Upload size={16} strokeWidth={2} aria-hidden />
                            <span>{uploadLabel}</span>
                        </label>
                    </div>
                </div>

                {uploadError ? (
                    <div className="abf-cover-modal__alert" role="alert">
                        {uploadError}
                    </div>
                ) : null}

                {loading ? (
                    <div className="abf-cover-modal__empty" role="status">
                        <p className="abf-cover-modal__empty-text">Loading uploaded images…</p>
                    </div>
                ) : error ? (
                    <div className="abf-cover-modal__empty" role="alert">
                        <p className="abf-cover-modal__empty-text">{error}</p>
                        <button type="button" className="abf-cover-modal__empty-btn" onClick={onRetry}>
                            Try again
                        </button>
                    </div>
                ) : filteredPaths.length === 0 ? (
                    <div className="abf-cover-modal__empty" role="status">
                        <div className="abf-cover-modal__empty-visual" aria-hidden>
                            <Search size={36} strokeWidth={1.5} />
                        </div>
                        {totalImages === 0 ? (
                            <p className="abf-cover-modal__empty-text">
                                No images in <code className="abf-cover-modal__code">uploads/blog-images</code> yet.
                                Upload a cover from the form first.
                            </p>
                        ) : (
                            <>
                                <p className="abf-cover-modal__empty-text">
                                    Nothing matches <strong>“{query.trim()}”</strong>.
                                </p>
                                <button
                                    type="button"
                                    className="abf-cover-modal__empty-btn"
                                    onClick={() => setQuery("")}
                                >
                                    Reset search
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="abf-cover-modal__body">
                        <aside className="abf-cover-modal__preview" aria-live="polite">
                            <div className="abf-cover-modal__preview-inner">
                                <div className="abf-cover-modal__preview-badges">
                                    {previewIsForeignHover ? (
                                        <span className="abf-cover-modal__badge abf-cover-modal__badge--live">
                                            Live preview
                                        </span>
                                    ) : previewMatchesSelection ? (
                                        <span className="abf-cover-modal__badge abf-cover-modal__badge--ok">
                                            <Check size={12} strokeWidth={3} aria-hidden />
                                            Current selection
                                        </span>
                                    ) : (
                                        <span className="abf-cover-modal__badge">Preview</span>
                                    )}
                                </div>
                                <div className="abf-cover-modal__preview-frame">
                                    <div className="abf-cover-modal__preview-shine" aria-hidden />
                                    {previewSrc ? (
                                        <img src={previewSrc} alt="" className="abf-cover-modal__preview-img" />
                                    ) : (
                                        <span className="abf-cover-modal__preview-fallback" aria-hidden>
                                            ?
                                        </span>
                                    )}
                                </div>
                                <div className="abf-cover-modal__preview-meta">
                                    <code className="abf-cover-modal__preview-key" title={previewName}>
                                        {previewName}
                                    </code>
                                </div>
                            </div>
                        </aside>

                        <div className="abf-cover-modal__grid-col" onMouseLeave={() => setHoverPath(null)}>
                            <div className="abf-cover-modal__grid">
                                {filteredPaths.map((pathValue) => {
                                    const src = resolveBlogImageSrc(pathValue);
                                    const name = basenameFromPath(pathValue);
                                    const selected = selectedPath === pathValue;
                                    return (
                                        <button
                                            key={pathValue}
                                            type="button"
                                            className={`abf-cover-modal__tile ${selected ? "is-selected" : ""} ${
                                                hoverPath === pathValue ? "is-hover" : ""
                                            }`}
                                            onClick={() => onPick(pathValue)}
                                            onMouseEnter={() => setHoverPath(pathValue)}
                                            onFocus={() => setHoverPath(pathValue)}
                                            aria-pressed={selected}
                                            aria-label={`Use ${name} as cover`}
                                            title={name}
                                        >
                                            <span className="abf-cover-modal__tile-shine" aria-hidden />
                                            <span className="abf-cover-modal__thumb-wrap">
                                                {selected ? (
                                                    <span className="abf-cover-modal__tile-check">
                                                        <Check size={13} strokeWidth={3} aria-hidden />
                                                    </span>
                                                ) : null}
                                                {src ? (
                                                    <img
                                                        src={src}
                                                        alt=""
                                                        className="abf-cover-modal__thumb"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <span className="abf-cover-modal__thumb-fallback" aria-hidden>
                                                        ?
                                                    </span>
                                                )}
                                                <span className="abf-cover-modal__thumb-scrim" aria-hidden />
                                            </span>
                                            <span className="abf-cover-modal__filename">{name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                <footer className="abf-cover-modal__footer">
                    <span className="abf-cover-modal__footer-item">
                        <MousePointerClick size={15} strokeWidth={2} aria-hidden />
                        Click a tile to set cover
                    </span>
                    <span className="abf-cover-modal__footer-dot" aria-hidden />
                    <span className="abf-cover-modal__footer-item">
                        <Keyboard size={15} strokeWidth={2} aria-hidden />
                        Esc to close
                    </span>
                </footer>
            </div>
        </div>,
        document.body
    );
}
